import React, { useState, useEffect } from 'react';
import { getData, putData } from '../../services/api.service';
import { toastSuccess, toastError } from '../common/Toast';

function CompanySettingsManager() {
  const [settings, setSettings] = useState({
    company_name: '',
    company_cuit: '',
    company_address: '',
    company_email: '',
    company_phone: '',
    remito_footer_text: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoadingData(true);
      const response = await getData('/api/company-settings', false);
      
      if (response?.data?.success && response.data.data) {
        // Convertir array a objeto
        const settingsObject = {};
        response.data.data.forEach(setting => {
          settingsObject[setting.setting_key] = setting.setting_value;
        });
        setSettings(settingsObject);
      }
    } catch (error) {
      console.error('Error cargando configuraciones:', error);
      toastError('Error al cargar las configuraciones');
    } finally {
      setLoadingData(false);
    }
  };

  const handleChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      const response = await putData('/api/company-settings', settings, false);
      
      if (response?.data?.success) {
        toastSuccess('Configuraciones actualizadas exitosamente');
      } else {
        toastError('Error al actualizar configuraciones');
      }
    } catch (error) {
      console.error('Error guardando configuraciones:', error);
      toastError('Error al guardar las configuraciones');
    } finally {
      setLoading(false);
    }
  };

  if (loadingData) {
    return (
      <div className="container-fluid mt-4">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid mt-4">
      <div className="row">
        <div className="col-12">
          <div className="card shadow-sm">
            <div className="card-header bg-primary text-white">
              <h4 className="mb-0">
                <i className="bi bi-building me-2"></i>
                Configuración de Empresa
              </h4>
              <small>Datos que se utilizan en remitos y documentos oficiales</small>
            </div>
            
            <div className="card-body">
              <form onSubmit={handleSubmit}>
                <div className="row g-3">
                  {/* Razón Social */}
                  <div className="col-md-6">
                    <label className="form-label fw-bold">
                      <i className="bi bi-building me-2"></i>
                      Razón Social / Nombre Comercial *
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={settings.company_name || ''}
                      onChange={(e) => handleChange('company_name', e.target.value)}
                      placeholder="DORMO HOGAR"
                      required
                    />
                    <small className="text-muted">Aparece en encabezado de remitos</small>
                  </div>

                  {/* CUIT */}
                  <div className="col-md-6">
                    <label className="form-label fw-bold">
                      <i className="bi bi-card-text me-2"></i>
                      CUIT *
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={settings.company_cuit || ''}
                      onChange={(e) => handleChange('company_cuit', e.target.value)}
                      placeholder="27-35573639-9"
                      required
                    />
                    <small className="text-muted">Formato: XX-XXXXXXXX-X</small>
                  </div>

                  {/* Dirección */}
                  <div className="col-12">
                    <label className="form-label fw-bold">
                      <i className="bi bi-geo-alt me-2"></i>
                      Dirección Completa *
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={settings.company_address || ''}
                      onChange={(e) => handleChange('company_address', e.target.value)}
                      placeholder="Villa del Mar 5010 - Córdoba Capital"
                      required
                    />
                    <small className="text-muted">Incluir calle, número, ciudad y provincia</small>
                  </div>

                  {/* Email */}
                  <div className="col-md-6">
                    <label className="form-label fw-bold">
                      <i className="bi bi-envelope me-2"></i>
                      Email de Contacto *
                    </label>
                    <input
                      type="email"
                      className="form-control"
                      value={settings.company_email || ''}
                      onChange={(e) => handleChange('company_email', e.target.value)}
                      placeholder="dormo.online@gmail.com"
                      required
                    />
                  </div>

                  {/* Teléfono */}
                  <div className="col-md-6">
                    <label className="form-label fw-bold">
                      <i className="bi bi-telephone me-2"></i>
                      Teléfono de Contacto *
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={settings.company_phone || ''}
                      onChange={(e) => handleChange('company_phone', e.target.value)}
                      placeholder="3515081331"
                      required
                    />
                    <small className="text-muted">Sin guiones ni espacios</small>
                  </div>

                  {/* Texto pie de remito */}
                  <div className="col-12">
                    <label className="form-label fw-bold">
                      <i className="bi bi-chat-square-text me-2"></i>
                      Texto Pie de Remito (Opcional)
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={settings.remito_footer_text || ''}
                      onChange={(e) => handleChange('remito_footer_text', e.target.value)}
                      placeholder="ORIGINAL / DUPLICADO PARA TRANSPORTE"
                    />
                    <small className="text-muted">Texto adicional que aparecerá al pie de los remitos</small>
                  </div>
                </div>

                {/* Vista previa */}
                <div className="alert alert-info mt-4">
                  <h6 className="mb-2">
                    <i className="bi bi-eye me-2"></i>
                    Vista Previa del Remito:
                  </h6>
                  <div className="border p-3 bg-white text-dark" style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                    <div className="fw-bold">REMITO // {settings.company_name || '[NOMBRE EMPRESA]'}</div>
                    <div>Razón Social: {settings.company_name || '[NOMBRE EMPRESA]'}</div>
                    <div>CUIT: {settings.company_cuit || '[XX-XXXXXXXX-X]'}</div>
                    <hr className="my-2"/>
                    <div className="fw-bold">REMITENTE</div>
                    <div>DIRECCIÓN: {settings.company_address || '[DIRECCIÓN]'}</div>
                    <div>CONTACTO: {settings.company_email || '[EMAIL]'}</div>
                    <div>Tel: {settings.company_phone || '[TELÉFONO]'}</div>
                  </div>
                </div>

                {/* Botones */}
                <div className="d-flex gap-2 justify-content-end mt-4">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={loadSettings}
                    disabled={loading}
                  >
                    <i className="bi bi-arrow-counterclockwise me-2"></i>
                    Recargar
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Guardando...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-save me-2"></i>
                        Guardar Cambios
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Información adicional */}
          <div className="card shadow-sm mt-3">
            <div className="card-header bg-light">
              <h6 className="mb-0">
                <i className="bi bi-info-circle me-2"></i>
                Información Importante
              </h6>
            </div>
            <div className="card-body">
              <ul className="mb-0">
                <li>Estos datos se utilizan automáticamente al generar remitos desde los pedidos</li>
                <li>Los cambios se reflejarán inmediatamente en todos los remitos nuevos</li>
                <li>Asegúrate de que el CUIT esté en el formato correcto (XX-XXXXXXXX-X)</li>
                <li>El número de teléfono debe ser solo dígitos, sin guiones ni espacios</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CompanySettingsManager;
